import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, Divider } from '@mui/material';
import type { ApiCountry, ApiOrganization } from '@agunity/api-v4';

import AgTypography from '../../../stories/Styles/AgTypography';
import AgProfileSectionTable from '../../../stories/Components/AgSimpleTable';
import defaultAvatar from '../../../stories/Components/AgAvatar/defaultAvatar.svg';
import AgAvatar from '../../../stories/Components/AgAvatar';
import AgToggle from '../../../stories/Components/AgToggle';
import AgButton from '../../../stories/Components/AgButton';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { MEMBER_FIELD_NAME_NOTES } from '../../../Constants';
import { DropdownOptionsProps } from '../types';
import { getCountries } from '../../../redux/slices/countries';
import { setIsOpenViewAvatar, setOrgCountryOptions } from '../../../redux/slices/organizations';
import { AgColor } from '../../../stories/Styles/AgColor';

export interface ViewOrgDetailsProps {
  identity?: ApiOrganization;
  imageURL?: string;
}

const ViewOrganizationDetails = ({ identity, imageURL, }: ViewOrgDetailsProps) => {
  const messages = useIntl().formatMessage;
  const longitude = null;
  const latitude = null;
  const dispatch = useAppDispatch();

  const countryOptions: DropdownOptionsProps[] =
    useAppSelector((state) => state.staff.countryOptions)?.map((country) => ({
      id: country.id ?? -1,
      name: country.name ?? ''
    })) || [];
  const [countries, setCountries] = useState<DropdownOptionsProps[]>(countryOptions);
  const [selectedCountry, setSelectedCountry] = useState<ApiCountry>();
  
  useEffect(() => {
    const f = async () => {
      try {
        if (countries?.length === 0) {
          const cou = await dispatch(getCountries());
          if (cou?.data?.length) {
            const countryList = cou.data.map((country) => ({
              name: country.name ?? '',
              id: country.id ?? -1
            }));
            setCountries(countryList);
            dispatch(setOrgCountryOptions(cou.data));
          }
        }
        if (countries.length && identity?.address?.countryId) {
          const countryObj = countries.find(
            (country) => country.id === identity?.address?.countryId
          );
          countryObj && setSelectedCountry(countryObj);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };
    f();
  }, [countries, dispatch, identity?.address?.countryId]);

  const orgNameData = [
    { name: messages({ id: 'CERTIFICATION_ORG_NAME' }), value: identity?.name || '' },
    { name: messages({ id: 'DESCRIPTION' }), value: identity?.publicDescription || '' },
    { name: messages({ id: 'MEMBER_PERSONAL_DETAILS_PHOTO' }), value: (
      <AgTypography
        style={{ cursor: 'pointer' }}
        onClick={() => {
          dispatch(setIsOpenViewAvatar(true));
        }}
      >
        {imageURL ? (
          <img
            src={imageURL}
            alt={messages({ id: 'MEMBER_PERSONAL_DETAILS_PHOTO' })}
            style={{ width: '32px', height: '32px',  verticalAlign: 'middle', objectFit: 'cover'  }}
          />
        ) : (
          <AgAvatar
            src={defaultAvatar}
            alt={messages({ id: 'AVATAR_ALT' })}
            style={{ width: '32px', height: '32px', verticalAlign: 'middle', objectFit: 'cover' }}
          />
        )}
      </AgTypography>
    )
    },
    { 
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_NOTES' }), 
      value: identity?.data?.find((item) => item.key === MEMBER_FIELD_NAME_NOTES) ?.value || '' 
    },
  ];

  const addressData = [
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_COUNTRY' }),
      value: selectedCountry?.name ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_PROVINCE' }),
      value: identity?.address?.province ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_CITY' }),
      value: identity?.address?.city ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_POSTAL_CODE' }),
      value: identity?.address?.postCode ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_1' }),
      value: identity?.address?.addressLine1 ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_2' }),
      value: identity?.address?.addressLine2 ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_3' }),
      value: identity?.address?.addressLine3 ?? ''
    }
  ];

  const GPSData = [
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_LATITUDE' }),
      value: identity?.address?.location?.X ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_LONGITUDE' }),
      value: identity?.address?.location?.Y ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_ALTITUDE' }),
      value: identity?.address?.location?.altitude ?? ''
    },
    {
      name: messages({ id: 'PRECISION' }),
      value: identity?.address?.location?.accuracy ?? ''
    }
  ];

  const contactData = [
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_CONTACT_INFO_PHONE' }),
      value: identity?.phoneNumber ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_CONTACT_INFO_EMAIL' }),
      value: identity?.salesEmail ?? ''
    }
  ];

  return ( 
    <AgTypography
      style={{
        padding: '24px 16px',
        display: 'grid',
        gap: '16px'
      }}
    >
      <AgProfileSectionTable
        tableData={orgNameData}
        title={messages({ id: 'CERTIFICATION_ORG_NAME' })}
        style={{ maxWidth: '616px' }}
      />
      <Divider sx={{ margin: '16px 0' }} />
      
      <AgTypography
        contentStyle='AP.H5.UPPERCASE.Bold'
        message={messages({ id: 'MAKE_DETAILS_PUBLIC' })}
        style={{
          color: AgColor.grey6
        }}
      />

      <AgTypography
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <AgTypography
            contentStyle={'AP.Paragraph.Small.Regular'}
            // Will replace the text once the right text is available
            message={messages({ id: 'MAKE_DETAILS_PUBLIC_DESCRIPTION_1' })}
            style={{
              width: '320px',
              color: AgColor.grey7
            }}
          />
        </Box>
        <AgToggle
          /** TODO: Apply the right value */
          checked={false}
          onChange={() => {}}
          disabled
        />
      </AgTypography>
      <Divider sx={{ margin: '16px 0' }} />
      <AgProfileSectionTable
        tableData={addressData}
        title={messages({ id: 'ADDRESS' })}
        style={{ maxWidth: '616px' }}
      />
      <Divider sx={{ margin: '16px 0' }} />
        
      <AgProfileSectionTable
        tableData={GPSData}
        title={messages({ id: 'GPS_LOCATION' })}
        style={{ maxWidth: '616px' }}
      />
      
      <AgTypography
        style={{
          display: 'flex',
          alignItems: 'start',
          justifyContent: 'end',
          gap: '16px'
        }}
      >

        <AgButton
          label={messages({ id: 'VIEW_GPS_LOCATION_ON_MAP' })}
          disabled={!Number(longitude) || !Number(latitude)}
          variant={'outlined'}
          color={'secondary'}
          small
          onClick={() => {
            if (!longitude || !latitude) return;
            const mapUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
            window.open(mapUrl, '_blank');
          }}
        />
      </AgTypography>
      <Divider sx={{ margin: '16px 0' }} />
        
      <AgProfileSectionTable
        tableData={contactData}
        title={messages({ id: 'MEMBER_PERSONAL_DETAILS_CONTACT_TITLE' })}
        style={{ maxWidth: '616px' }}
      />
      
    </AgTypography>
  );
};
 
export default ViewOrganizationDetails;